import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import type { RefObject } from 'react';
import { useRouter } from 'next/router';
import { SectionHeader } from '../SectionHeader';
import {
  GetPashaFilterDataQuery,
  PashaItemsQuery
} from '../../../graphql/graphql';
import { getPashaItems } from '../../../api/queries/pasha';
import useScreenSize from '../../../hooks/useScreenSize';
import Pagination from '../../ui/Pagination';
import { SortOption } from '../../../graphql/types';
import { useOnScreen } from '../../../hooks/useOnScreen';
import {
  getCurrentSortOption,
  PASHA_LANDING_SLUG
} from '../../../utils/helpers/search';
import { FilterPanel } from './FilterPanel';
import { CardPlaceholder } from './CardPlaceholder';
import { FilterResult } from './FilterResult';
import { useClient } from '../../../hooks/useClient';
import { OffersSectionContainer } from './styled';

enum ItemCount {
  desk = 10,
  mob = 6
}

export const OffersSection: React.FC<{
  pashaItems?: PashaItemsQuery['items'];
  totalCount: number;
  setVisibleTabRef: React.Dispatch<
    React.SetStateAction<RefObject<HTMLDivElement | null> | null>
  >;
  setPashaItemsRef: React.Dispatch<
    React.SetStateAction<RefObject<HTMLDivElement | null> | null>
  >;
  sortOption: SortOption;
  currentFilterValues?: unknown;
  filtersParams?: GetPashaFilterDataQuery;
}> = ({
  sortOption,
  currentFilterValues,
  filtersParams,
  pashaItems,
  totalCount,
  setPashaItemsRef,
  setVisibleTabRef
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { isLarge, isSmall, isExtraSmall } = useScreenSize();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isVisible = useOnScreen(wrapperRef);
  const isClient = useClient();
  const firstRunRef = useRef(true);

  const [filterValues, setFilterValues] = useState(currentFilterValues);
  const limitForFetch = !isClient || isLarge ? ItemCount.desk : ItemCount.mob;

  const isPashaPage = router.pathname === '/pasha/[[...search]]';

  const [currentPage, setCurrentPage] = useState(1);
  const [currTotalCount, setCurrTotalCount] = useState(
    Math.ceil(totalCount / ItemCount.desk) || 0
  );

  const currSortOption = useMemo(
    () => getCurrentSortOption(filtersParams, router?.query, sortOption),
    [filtersParams, router?.query, sortOption]
  );

  const [fetchPashaItems, { loading, data }] = useLazyQuery(getPashaItems, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: res => {
      const count =
        Math.ceil(res.itemsConnection?.totalCount / limitForFetch) || 0;
      if (count !== currTotalCount) {
        setCurrTotalCount(count);
      }
      if (currentPage > count) {
        setCurrentPage(count || 1);
      }
    }
  });

  useEffect(() => {
    if (!router.isReady) return;

    const safeFilterValues =
      filterValues && typeof filterValues === 'object'
        ? (filterValues as Record<string, unknown>)
        : {};

    const variables = {
      limit: limitForFetch,
      sort: currSortOption?.name,
      connectionSort: currSortOption?.name,
      filter: { landingBrandSlug: PASHA_LANDING_SLUG, ...safeFilterValues },
      offset: (currentPage - 1) * limitForFetch
    };

    if (firstRunRef.current) {
      firstRunRef.current = false;
      if (variables.limit === ItemCount.desk && variables.offset === 0) return;
    }
    fetchPashaItems({ variables });
  }, [
    router.isReady,
    limitForFetch,
    currentPage,
    filterValues,
    currSortOption,
    fetchPashaItems
  ]);

  useEffect(() => {
    setPashaItemsRef?.(wrapperRef);
    if (isVisible) return setVisibleTabRef?.(wrapperRef);
  }, [isVisible]);

  const items = data?.items ?? pashaItems ?? [];

  return (
    <OffersSectionContainer ref={wrapperRef}>
      <SectionHeader title={t('pasha.offers_section.title')} />
      {filtersParams && (
        <FilterPanel
          {...{ filterValues, filtersParams, currSortOption, setFilterValues }}
        />
      )}
      {loading ? (
        <CardPlaceholder
          isLarge={isLarge}
          isSmall={isSmall}
          isExtraSmall={isExtraSmall}
        />
      ) : (
        <FilterResult pashaItems={items} />
      )}
      {isClient && currTotalCount > 1 && (
        <Pagination
          totalPages={currTotalCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          isPashaLanding={isPashaPage}
        />
      )}
    </OffersSectionContainer>
  );
};
